import React, { useEffect, useState } from 'react';
import { FileTextIcon, VideoIcon, MicIcon, ImageIcon, ArrowDownIcon, CpuIcon, SparklesIcon, WifiOffIcon, DownloadIcon, SmartphoneIcon } from 'lucide-react';
import { Card, CardContent } from './ui/Card';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function FeaturesSection() {
  const revealRef = useScrollReveal();
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setAnimated(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    const bars = document.getElementById('engine-progress-bars');
    if (bars) observer.observe(bars);
    return () => observer.disconnect();
  }, []);
  const progressBars = [{
    label: 'Rozpoznanie Textu',
    value: '99%',
    width: '99%'
  }, {
    label: 'Analýza Obrazu',
    value: '97%',
    width: '97%'
  }, {
    label: 'Porozumenie Videu',
    value: '94%',
    width: '94%'
  }, {
    label: 'Spracovanie Zvuku',
    value: '92%',
    width: '92%'
  }, {
    label: 'Krížové Vyhľadávanie',
    value: '96%',
    width: '96%'
  }];
  return <section id="engine" ref={revealRef} className="py-36 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden" data-id="element-32" data-mp="3g0lgevn3np4c">
      {/* Block A: Engine Intro */}
      <div className="max-w-4xl mx-auto text-center mb-28" data-id="element-33" data-mp="2nbehhvjqgxik">
        <p className="reveal text-xs font-semibold tracking-[0.2em] text-apple-gray uppercase mb-8" data-id="element-34" data-mp="2nbwlxfgjui1k">
          CORE TECHNOLOGY
        </p>
        <h2 className="reveal reveal-delay-1 text-5xl md:text-7xl font-extrabold tracking-tight text-apple-black mb-6" data-id="element-35" data-mp="2x9kkkgha09p0">
          Gemini Multimodal Embeddings 2
        </h2>
        <p className="reveal reveal-delay-2 text-2xl md:text-3xl font-light text-apple-black mb-8" data-id="element-36" data-mp="7mmoq675upwn">
          Jeden engine. Štyri modality. Nekonečné možnosti.
        </p>
        <p className="reveal reveal-delay-3 text-lg md:text-xl text-apple-gray leading-relaxed mb-16 font-light" data-id="element-37" data-mp="28l6m7rm1dxsj">
          Predstavte si technológiu, ktorá rozumie svetu rovnako ako vy — vidí
          obrázky, počúva zvuky, číta texty a pozerá videá. Všetko naraz, v
          jednom okamihu. Google túto technológiu vyvinul. My z nej staviame
          reálne produkty pre reálnych ľudí.
        </p>

        {/* AI Engine Visualization Image */}
        <div className="reveal-scale reveal-delay-4 w-full rounded-[2rem] overflow-hidden shadow-xl" data-id="element-38" data-mp="1kxfaap4ermrs">
          <img src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=500&fit=crop&crop=center" alt="AI neurónová sieť vizualizácia" className="w-full h-48 md:h-80 object-cover hover:scale-105 transition-transform duration-700" data-id="element-39" data-mp="1yobsdm0pklkl" />
        </div>
      </div>

      {/* Block B: Visual Modality Diagram */}
      <div className="reveal relative max-w-3xl mx-auto md:h-[400px] mb-36 flex flex-col md:block items-center justify-center gap-8" data-id="element-40" data-mp="124l5go0i1b1a">
        {/* Center AI Circle */}
        <div className="relative md:absolute md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-20 w-36 h-36 bg-white rounded-full shadow-2xl flex items-center justify-center border-4 border-gray-50" data-id="element-41" data-mp="wu8yijfuic17">
          <div className="absolute inset-0 rounded-full bg-apple-blue/20 animate-pulse" data-id="element-42" data-mp="n3xk22yjckg2"></div>
          <div className="absolute inset-2 rounded-full bg-apple-blue/10 animate-pulse" style={{
          animationDelay: '0.5s'
        }} data-id="element-43" data-mp="1atrukdvdtwx5"></div>
          <span className="text-3xl font-extrabold text-apple-black relative z-10 tracking-tighter" data-id="element-44" data-mp="10u9fobubx1by">
            AI
          </span>
        </div>

        {/* Connecting Lines (Desktop Only) */}
        <div className="hidden md:flex absolute inset-0 items-center justify-center z-0" data-id="element-45" data-mp="3lkiaw151b3gk">
          <div className="w-full h-full max-w-[400px] max-h-[400px] border-2 border-dashed border-gray-200 rounded-full rotate-45" data-id="element-46" data-mp="3qlfnly79z5ao"></div>
          <div className="absolute w-full h-full max-w-[400px] max-h-[400px] border-2 border-dashed border-gray-200 rounded-full -rotate-45" data-id="element-47" data-mp="ao7wzhd675yi"></div>
        </div>

        {/* 4 Modality Boxes with animated glows - Responsive Layout */}
        <div className="grid grid-cols-2 gap-4 w-full px-4 md:px-0 md:block" data-id="element-48" data-mp="3s34wl06o4wu2">
          <div className="relative md:absolute md:top-0 md:left-12 w-full md:w-32 h-32 bg-white rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center justify-center z-10 overflow-hidden card-hover-premium" data-id="element-49" data-mp="tkzb7cduuuvp">
            <div className="modality-glow-blue w-full h-14 absolute top-0 rounded-t-2xl" data-id="element-50" data-mp="jma145s7aeq0"></div>
            <FileTextIcon className="w-8 h-8 text-apple-blue mb-2 relative z-10" data-id="element-51" data-mp="eo88ou8vx31v" />
            <span className="font-bold text-sm text-apple-black relative z-10" data-id="element-52" data-mp="34d0tqv1b1144">
              TEXT
            </span>
          </div>
          <div className="relative md:absolute md:top-0 md:right-12 w-full md:w-32 h-32 bg-white rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center justify-center z-10 overflow-hidden card-hover-premium" data-id="element-53" data-mp="2zcwbazgu5wb9">
            <div className="modality-glow-purple w-full h-14 absolute top-0 rounded-t-2xl" data-id="element-54" data-mp="1jvjbvnw018d6"></div>
            <VideoIcon className="w-8 h-8 text-purple-500 mb-2 relative z-10" data-id="element-55" data-mp="1osi4g200l799" />
            <span className="font-bold text-sm text-apple-black relative z-10" data-id="element-56" data-mp="1tlumfta6uft1">
              VIDEO
            </span>
          </div>
          <div className="relative md:absolute md:bottom-0 md:left-12 w-full md:w-32 h-32 bg-white rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center justify-center z-10 overflow-hidden card-hover-premium" data-id="element-57" data-mp="1ylywp1ki6eat">
            <div className="modality-glow-amber w-full h-14 absolute top-0 rounded-t-2xl" data-id="element-58" data-mp="2cjf4agejce45"></div>
            <MicIcon className="w-8 h-8 text-amber-500 mb-2 relative z-10" data-id="element-59" data-mp="d6t41srzz1e1" />
            <span className="font-bold text-sm text-apple-black relative z-10" data-id="element-60" data-mp="d8nnf2nmelg0">
              AUDIO
            </span>
          </div>
          <div className="relative md:absolute md:bottom-0 md:right-12 w-full md:w-32 h-32 bg-white rounded-2xl shadow-lg border border-gray-100 flex flex-col items-center justify-center z-10 overflow-hidden card-hover-premium" data-id="element-61" data-mp="a4eu3v1vym0w">
            <div className="modality-glow-emerald w-full h-14 absolute top-0 rounded-t-2xl" data-id="element-62" data-mp="k3xbq4ubpvps"></div>
            <ImageIcon className="w-8 h-8 text-emerald-500 mb-2 relative z-10" data-id="element-63" data-mp="f39olu97tz0u" />
            <span className="font-bold text-sm text-apple-black relative z-10" data-id="element-64" data-mp="t30vzc588h5v">
              IMAGE
            </span>
          </div>
        </div>
      </div>

      {/* Block C: How It Works */}
      <div className="mb-36" data-id="element-65" data-mp="12tvkkn5y3suh">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative" data-id="element-66" data-mp="12t40a8hsthji">
          <div className="hidden md:block absolute top-8 left-[16%] right-[16%] h-0.5 border-t-2 border-dashed border-gray-200 z-0" data-id="element-67" data-mp="xu99okdlaoou"></div>

          <div className="reveal flex flex-col items-center text-center relative z-10" data-id="element-68" data-mp="1cs1ofabbb91z">
            <div className="w-16 h-16 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center mb-6" data-id="element-69" data-mp="17u9rg1vt8wqx">
              <span className="text-xl font-extrabold text-apple-gray" data-id="element-70" data-mp="1vg3nx5lz6v7d">01</span>
            </div>
            <ArrowDownIcon className="w-8 h-8 text-apple-blue mb-4" data-id="element-71" data-mp="1hgb3fs11lb7u" />
            <h3 className="text-2xl font-bold text-apple-black mb-3" data-id="element-72" data-mp="1hg2pdwt6nzv2">Vstup</h3>
            <p className="text-apple-gray leading-relaxed font-light" data-id="element-73" data-mp="1me46wdaynr4f">
              Pošlete text, obrázok, video alebo zvuk. Alebo všetko naraz.
            </p>
          </div>

          <div className="reveal reveal-delay-2 flex flex-col items-center text-center relative z-10" data-id="element-74" data-mp="1chhmtqqgl43g">
            <div className="w-16 h-16 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center mb-6" data-id="element-75" data-mp="yqmcsb484gya">
              <span className="text-xl font-extrabold text-apple-gray" data-id="element-76" data-mp="13p5z4cmqbur6">02</span>
            </div>
            <CpuIcon className="w-8 h-8 text-apple-blue mb-4" data-id="element-77" data-mp="osf8osv19tdp" />
            <h3 className="text-2xl font-bold text-apple-black mb-3" data-id="element-78" data-mp="tqzzk1oeb0ph">
              Spracovanie
            </h3>
            <p className="text-apple-gray leading-relaxed font-light" data-id="element-79" data-mp="197q2vlxw96pi">
              Engine vytvorí jednotný vektor — digitálny odtlačok významu, nie
              slov.
            </p>
          </div>

          <div className="reveal reveal-delay-4 flex flex-col items-center text-center relative z-10" data-id="element-80" data-mp="106rz94rdb17g">
            <div className="w-16 h-16 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center mb-6" data-id="element-81" data-mp="v86z15c48hj0">
              <span className="text-xl font-extrabold text-apple-gray" data-id="element-82" data-mp="qbqc2egnk97i">03</span>
            </div>
            <SparklesIcon className="w-8 h-8 text-apple-blue mb-4" data-id="element-83" data-mp="lhcblxn4vbvz" />
            <h3 className="text-2xl font-bold text-apple-black mb-3" data-id="element-84" data-mp="hi3imuzx3i1h">Výstup</h3>
            <p className="text-apple-gray leading-relaxed font-light" data-id="element-85" data-mp="cjjvir9l807j">
              Dostanete odpoveď, ktorá rozumie kontextu naprieč všetkými
              formátmi.
            </p>
          </div>
        </div>
      </div>

      {/* Block D: Capability Progress Bars */}
      <div id="engine-progress-bars" className="reveal max-w-3xl mx-auto mb-44" data-id="element-86" data-mp="3om0m7t43g35d">
        <h3 className="text-3xl md:text-4xl font-bold text-center text-apple-black mb-14 tracking-tight" data-id="element-87" data-mp="3oo4mm12sak0w">
          Výkon Enginu v Číslach
        </h3>
        <div className="space-y-8" data-id="element-88" data-mp="2fvd7e99mqwn">
          {progressBars.map((bar, index) => <div key={index} className="w-full" data-id="element-89" data-mp="7dww9z6oyq84" data-mpi={index}>
              <div className="flex justify-between items-end mb-3" data-id="element-90" data-mp="7bkdjjt40bu0">
                <span className="text-base font-semibold text-apple-black" data-id="element-91" data-mp="35wlkrxkhxc0g">
                  {bar.label}
                </span>
                <span className="text-lg font-bold text-apple-blue" data-id="element-92" data-mp="3as9emq9xatca">
                  {bar.value}
                </span>
              </div>
              <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-93" data-mp="3kkor582gapps">
                <div className="h-full bg-gradient-to-r from-apple-blue to-blue-400 rounded-full" style={{
              width: animated ? bar.width : '0%',
              transition: `width 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.15}s`
            }} data-id="element-94" data-mp="2n515tydad25i" />
              </div>
            </div>)}
        </div>
      </div>

      {/* Block E: PWA Showcase */}
      <div id="pwa" className="w-full pt-20 border-t border-gray-100" data-id="element-95" data-mp="1w9bf538q2lfl">
        <div className="reveal text-center mb-20" data-id="element-96" data-mp="20a3mfqy1ag98">
          <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight text-apple-black" data-id="element-97" data-mp="207hie1acqivr">
            Prečo PWA? Pretože budúcnosť je v prehliadači.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-98" data-mp="18ixit0j3u0f7">
          <div className="reveal" data-id="element-99" data-mp="1cjr89yc73na7">
            <Card className="border-gray-200 rounded-3xl bg-white shadow-sm border overflow-hidden card-hover-premium" data-id="element-100" data-mp="1cig2xu33q7gz">
              <img src="https://images.unsplash.com/photo-1526498460520-4c246339dccb?w=600&h=300&fit=crop&crop=center" alt="Offline práca" className="w-full h-40 object-cover hover:scale-105 transition-transform duration-500" data-id="element-101" data-mp="1hcke6pjeh1nv" />
              <CardContent className="p-10 text-center flex flex-col items-center" data-id="element-102" data-mp="h8tct6hgnwcn">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6 -mt-12 relative z-10 border-4 border-white shadow-md" data-id="element-103" data-mp="cccgvq40sdp0">
                  <WifiOffIcon className="w-8 h-8 text-apple-blue" data-id="element-104" data-mp="3bo2guapsbbl" />
                </div>
                <h4 className="text-2xl font-bold text-apple-black mb-4" data-id="element-105" data-mp="3uhz5ux375o63">
                  Funguje Offline
                </h4>
                <p className="text-apple-gray leading-relaxed font-light" data-id="element-106" data-mp="3ppxlenvvkr4z">
                  Vaša appka beží aj bez internetu. Dáta sa synchronizujú, keď
                  sa pripojíte.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="reveal reveal-delay-2" data-id="element-107" data-mp="3bqoeketk79do">
            <Card className="border-gray-200 rounded-3xl bg-white shadow-sm border overflow-hidden card-hover-premium" data-id="element-108" data-mp="3bq6ch1k7u0xu">
              <img src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&h=300&fit=crop&crop=center" alt="Mobilná inštalácia" className="w-full h-40 object-cover hover:scale-105 transition-transform duration-500" data-id="element-109" data-mp="36u7imyg4bqq1" />
              <CardContent className="p-10 text-center flex flex-col items-center" data-id="element-110" data-mp="2n85vi9n682g0">
                <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-6 -mt-12 relative z-10 border-4 border-white shadow-md" data-id="element-111" data-mp="2s5eh6k7j2om0">
                  <DownloadIcon className="w-8 h-8 text-purple-500" data-id="element-112" data-mp="3ku3c21hzpxpb" />
                </div>
                <h4 className="text-2xl font-bold text-apple-black mb-4" data-id="element-113" data-mp="365gudt4w4tux">
                  Inštalácia Jedným Klikom
                </h4>
                <p className="text-apple-gray leading-relaxed font-light" data-id="element-114" data-mp="3b2fmk64zuqqh">
                  Žiadny App Store, žiadne schvaľovanie. Používateľ klikne a
                  appka je na ploche.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="reveal reveal-delay-4" data-id="element-115" data-mp="1s4aqq4rlnmab">
            <Card className="border-gray-200 rounded-3xl bg-white shadow-sm border overflow-hidden card-hover-premium" data-id="element-116" data-mp="1s2zmk3u8scio">
              <img src="https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&h=300&fit=crop&crop=center" alt="Natívne funkcie" className="w-full h-40 object-cover hover:scale-105 transition-transform duration-500" data-id="element-117" data-mp="21uvx4edilv3s" />
              <CardContent className="p-10 text-center flex flex-col items-center" data-id="element-118" data-mp="147dgkhdm3op8">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6 -mt-12 relative z-10 border-4 border-white shadow-md" data-id="element-119" data-mp="149q6phtix5dd">
                  <SmartphoneIcon className="w-8 h-8 text-emerald-500" data-id="element-120" data-mp="1e4rx0gzjcdhj" />
                </div>
                <h4 className="text-2xl font-bold text-apple-black mb-4" data-id="element-121" data-mp="2tla37ofae34w">
                  Natívny Výkon
                </h4>
                <p className="text-apple-gray leading-relaxed font-light" data-id="element-122" data-mp="2pkgkrakm5gje">
                  Push notifikácie, prístup ku kamere, GPS, NFC — všetko čo
                  poznáte z natívnych appiek.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}